import { Injectable } from '@angular/core';
import { mimeTypeIcons } from '../common/constants/mime-type-icons';
import { BcRestApi } from 'common/api/rest-api';
import { Email } from 'common/xislivechat/models/email';
import { DynamicQueryDecoratorService } from 'common/dynamicquery/dynamic-query-decorator.service';
import { DynamicQueryAdapterService } from 'common/dynamicquery/dynamic-query-adapter.service';

@Injectable({
  providedIn: 'root'
})
export class EmailsService {
  constructor(
    private _dynamicQuery: DynamicQueryAdapterService,
    private _dynamicQueryDecorator: DynamicQueryDecoratorService,
    private bcRestApi: BcRestApi
  ) {
  }

  observeEmailByEmailId(emailId) {
    if (!emailId) {
      throw new Error('Missing emailId');
    }

    const messagesObservable = this._dynamicQuery.getQuery('emailByEmailID', { EmailID: emailId });
    return this._dynamicQueryDecorator.create(messagesObservable, Email.wrap);
  }

  observeEmailAttachmentsByEmailId(emailId) {
    if (!emailId) {
      throw new Error('Missing emailId');
    }

    const messagesObservable = this._dynamicQuery.getQuery('emailAttachmentsByEmailID', { EmailID: emailId });
    return this._dynamicQueryDecorator.create(messagesObservable, Email.wrap); // TODO EmailAttachments.wrap?
  }

  getEmailThread(emailThreadId) {
    return this.bcRestApi.getEmailThread(emailThreadId);
  }

  async getLastEmailFromThreadWithAttachments(emailThreadId) {
    const email = await this.getLastEmailFromThread(emailThreadId);
    const attachments = await this.getEmailAttachments(email.id);
    return this.buildEmailWithAttachments(email, attachments);
  }

  getLastEmailFromThread(emailThreadId) {
    return this.bcRestApi.getLastEmailFromThread(emailThreadId);
  }

  async getEmailWithAttachments(emailId: string) {
    const email = await this.getEmail(emailId);
    const attachments = await this.getEmailAttachments(emailId);
    return this.buildEmailWithAttachments(email, attachments);
  }

  async buildEmailWithAttachments(email, attachments) {
    let attachment = null;
    for (let i = 0; i < attachments.length; i++) {
      attachment = attachments[i];
      attachment.canDelete = false;
      attachment.downloadUrl = await this.getEmailAttachmentDownloadUrl(email.id, attachment.id, attachment.fileName);
      this.decorateEmailAttachment(attachment);
    }
    email.attachments = attachments;
    return email;
  }

  decorateEmailAttachment(attachment) {
    attachment.shortFileName = this.getProperlySizedNameForNameString(attachment.fileName);
    attachment.iconClass = this.getIconClassForContentType(attachment.contentType);
    return attachment;
  }

  getEmail(emailId) {
    return this.bcRestApi.getEmail(emailId);
  }

  getEmailAttachments(emailId) {
    return this.bcRestApi.getEmailAttachments(emailId);
  }

  getEmailAttachmentDownloadUrl(emailId, emailAttachmentId, attachmentFileName) {
    return this.bcRestApi.getEmailAttachmentDownloadUrl(emailId, emailAttachmentId, attachmentFileName);
  }

  acceptEmailThread(emailThreadId) {
    return this.bcRestApi.acceptEmailThread(emailThreadId);
  }

  closeEmailThread(emailThreadId) {
    return this.bcRestApi.closeEmailThread(emailThreadId);
  }

  sendEmailToThread(emailThreadId, emailId, details, emailAttachments) {
    return this.bcRestApi.sendMessageToEmailThread(emailThreadId, emailId, details, emailAttachments);
  }

  getIconClassForContentType(contentType) {
    if (contentType.indexOf(';') >= 0) {
      contentType = contentType.substring(0, contentType.indexOf(';'));
    }

    let returnVal = mimeTypeIcons[contentType];
    if (returnVal === undefined) {
      if (contentType.indexOf('audio/') >= 0) {
        returnVal = mimeTypeIcons['audio-default'];
      } else if (contentType.indexOf('video/') >= 0) {
        returnVal = mimeTypeIcons['video-default'];
      } else if (contentType.indexOf('image/') >= 0) {
        returnVal = mimeTypeIcons['image-default'];
      } else {
        returnVal = mimeTypeIcons.default;
      }
    }
    return returnVal;
  }

  getProperlySizedNameForNameString(name) {
    let returnString = null;

    if (name !== null && name !== '') {
      const fileNameMaxLength = 16;
      const fileNamePostfixToPrefixRatio = 1.0;

      const extension = name.substring(name.lastIndexOf('.') + 1, name.length).toLowerCase();
      const filenameNoExtension = name.replace('.' + extension, '');

      if (filenameNoExtension.length <= fileNameMaxLength) {
        returnString = name;
      } else {
        const prefixLength = Math.round(fileNameMaxLength * fileNamePostfixToPrefixRatio);
        const postfixLength = Math.round(fileNameMaxLength * (1 - fileNamePostfixToPrefixRatio));
        returnString =
          filenameNoExtension.substr(0, prefixLength) +
          '...' +
          filenameNoExtension.substr(filenameNoExtension.length - postfixLength, postfixLength) +
          '.' +
          extension;
      }
    }
    return returnString;
  }
}
